/**
 * SPA が翻訳リソース（i18n/locales/*.json）を持つロケール。
 *
 * 言語を追加するときはここに足し、`i18n/config.ts` の `resources` に JSON を登録する
 * （`satisfies Record<SupportedLanguage, ...>` で登録漏れをコンパイルエラーにしている）。
 */
export const SUPPORTED_LOCALES = [
  'ja_JP',
  'en_US',
  'en_SG',
  'zh_TW',
  'zh_CH',
  'ko_KR',
  'fr_FR',
  'it_IT',
  'es_ES',
  'de_DE',
] as const
export type SupportedLanguage = (typeof SUPPORTED_LOCALES)[number]

export const isSupportedLanguage = (code: string): code is SupportedLanguage =>
  (SUPPORTED_LOCALES as readonly string[]).includes(code)

export const convertLanguage = (lang: string): SupportedLanguage => {
  switch (lang) {
    case 'ja':
    case 'ja-JP':
      return 'ja_JP'
    case 'en':
    case 'en-US':
      return 'en_US'
    case 'en-SG':
      return 'en_SG'
    case 'zh':
    case 'zh-CN':
      return 'zh_CH'
    case 'zh-TW':
      return 'zh_TW'
    case 'ko':
    case 'ko-KR':
      return 'ko_KR'
    case 'fr':
    case 'fr-FR':
      return 'fr_FR'
    case 'it':
    case 'it-IT':
      return 'it_IT'
    case 'es':
    case 'es-ES':
      return 'es_ES'
    case 'de':
    case 'de-DE':
      return 'de_DE'
    default:
      return 'ja_JP'
  }
}

/**
 * ブラウザから検出したロケールを、キャンペーンが提供しているロケールに寄せる（#3601）。
 *
 * en_SG を追加したことで、シンガポールの端末（`en-SG`）は en_SG を検出するようになった。
 * しかし ja_JP / en_US しか提供していない既存キャンペーンでそのまま en_SG を保存すると、
 * UI は英語なのに API（`X-Local: en_SG`）は翻訳行が無く日本語本文を返す混在表示になり、
 * 言語ダイアログの選択中表示もどの項目にも一致しなくなる。
 *
 * - キャンペーンが検出ロケールを提供していればそのまま
 * - 提供していなければ、同じ言語（先頭 2 文字）の提供ロケールへ寄せる（en_SG → en_US、en_US → en_SG）
 * - それも無ければ検出ロケールをそのまま返す（従来挙動を維持。de_DE 等の扱いは別途検討）
 *
 * 寄せ先は SPA が翻訳リソースを持つロケール（SUPPORTED_LOCALES）に限る。
 * 管理画面には `en_GB` のような未対応コードも登録できるため、無条件に寄せると
 * UI は ja_JP にフォールバックし API の `X-Local` だけ未知コードになる混在表示が起きる。
 */
export const resolveCampaignLocale = (
  detected: SupportedLanguage,
  campaignCodes: readonly string[],
): SupportedLanguage => {
  if (campaignCodes.includes(detected)) return detected

  const language = detected.slice(0, 2)
  const sameLanguage = campaignCodes.filter(isSupportedLanguage).find((code) => code.slice(0, 2) === language)

  return sameLanguage ?? detected
}
